import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Trophy, AlertCircle } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useCallback, useEffect, useRef } from "react";
import { format } from "date-fns";

type Period = "daily" | "weekly" | "monthly" | "quarterly";
type RangeType = "rolling" | "calendar";

interface HistoryEntry {
  date: string;
  composite: number;
  scores: Record<string, number>;
}

interface ScorecardResult {
  composite: number;
  scores: {
    goals: number;
    habits: number;
    tasks: number;
    health: number;
    trackers: number;
  };
  coachMessage: string;
  strengths: string[];
  improvements: string[];
  priorityActions: string[];
}

interface ScorecardWidgetProps {
  widgetId?: string;
  period?: Period;
  rangeType?: RangeType;
  showTrend?: boolean;
  history?: HistoryEntry[];
}

const AREA_LABELS: Record<string, string> = {
  goals: "Goals",
  habits: "Habits",
  tasks: "Tasks",
  health: "Health",
  trackers: "Trackers",
};

const AREA_ORDER = ["goals", "habits", "tasks", "health", "trackers"];

function scoreColor(score: number): string {
  if (score >= 8) return "text-emerald-500";
  if (score >= 6) return "text-amber-500";
  if (score >= 4) return "text-orange-500";
  return "text-rose-500";
}

function scoreBgColor(score: number): string {
  if (score >= 8) return "bg-emerald-500";
  if (score >= 6) return "bg-amber-500";
  if (score >= 4) return "bg-orange-500";
  return "bg-rose-500";
}

function periodLabel(period: Period, rangeType: RangeType): string {
  const range = rangeType === "rolling" ? "Rolling" : "Calendar";
  const labels: Record<Period, string> = {
    daily: "Today",
    weekly: `${range} Week`,
    monthly: `${range} Month`,
    quarterly: `${range} Quarter`,
  };
  return labels[period];
}

const MAX_HISTORY = 20;

export function ScorecardWidget({
  widgetId,
  period = "weekly",
  rangeType = "rolling",
  showTrend = true,
  history = [],
}: ScorecardWidgetProps) {
  const savedToHistory = useRef(false);

  const { data, isLoading, isError, refetch, isFetching } = useQuery<ScorecardResult>({
    queryKey: ["/api/scorecard/analyze", widgetId, period, rangeType],
    queryFn: () =>
      apiRequest("POST", "/api/scorecard/analyze", { period, rangeType }).then(
        (r) => r.json()
      ),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Persist history in widget settings
  const settingsMutation = useMutation({
    mutationFn: (newHistory: HistoryEntry[]) =>
      apiRequest("PATCH", `/api/dashboard/widgets/${widgetId}`, {
        settings: { period, rangeType, showTrend, history: newHistory },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/widgets"] });
    },
  });

  const appendHistory = useCallback(
    (result: ScorecardResult) => {
      if (!widgetId) return;
      const entry: HistoryEntry = {
        date: new Date().toISOString(),
        composite: result.composite,
        scores: result.scores,
      };
      const newHistory = [...history, entry].slice(-MAX_HISTORY);
      settingsMutation.mutate(newHistory);
    },
    [widgetId, history, period, rangeType, showTrend, settingsMutation]
  );

  // Append on first successful load
  useEffect(() => {
    if (data && !savedToHistory.current) {
      savedToHistory.current = true;
      appendHistory(data);
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    savedToHistory.current = false; // allow next save
    const result = await refetch();
    if (result.data) {
      appendHistory(result.data);
    }
  };

  if (isLoading || isFetching) {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="flex items-center gap-3">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-3 w-36" />
          </div>
        </div>
        {AREA_ORDER.map((k) => (
          <Skeleton key={k} className="h-4 w-full" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-center gap-3">
        <AlertCircle className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Couldn't generate scorecard. Try refreshing.
        </p>
        <Button size="sm" variant="outline" onClick={handleRefresh}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Retry
        </Button>
      </div>
    );
  }

  const chartData = history.slice(-10).map((h) => ({
    date: format(new Date(h.date), "MMM d"),
    score: parseFloat(h.composite.toFixed(1)),
  }));

  return (
    <div className="space-y-4">
      {/* Header: composite score */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-14 w-14 items-center justify-center rounded-full text-white text-xl font-bold ${scoreBgColor(data.composite)}`}
          >
            {data.composite.toFixed(1)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-muted-foreground" />
              <span className={`text-lg font-bold ${scoreColor(data.composite)}`}>
                out of 10
              </span>
            </div>
            <Badge variant="outline" className="text-xs mt-0.5">
              {periodLabel(period, rangeType)}
            </Badge>
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleRefresh}
          disabled={isFetching}
          className="shrink-0"
          data-testid="button-scorecard-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Area scores */}
      <div className="space-y-2">
        {AREA_ORDER.map((key) => {
          const score = data.scores[key as keyof typeof data.scores] ?? 0;
          return (
            <div key={key} className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-16 shrink-0">
                {AREA_LABELS[key]}
              </span>
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${scoreBgColor(score)}`}
                  style={{ width: `${(score / 10) * 100}%` }}
                />
              </div>
              <span className={`text-xs font-medium w-6 text-right ${scoreColor(score)}`}>
                {score.toFixed(0)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Coach message */}
      {data.coachMessage && (
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
          {data.coachMessage}
        </p>
      )}

      {/* Trend line */}
      {showTrend && chartData.length >= 2 && (
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1 font-medium">
            Trend
          </p>
          <ResponsiveContainer width="100%" height={60}>
            <LineChart data={chartData} margin={{ top: 4, right: 4, left: -30, bottom: 0 }}>
              <XAxis
                dataKey="date"
                tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[0, 10]}
                tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 11,
                  padding: "4px 8px",
                  borderRadius: 6,
                }}
                labelStyle={{ fontSize: 10 }}
                formatter={(v: number) => [v.toFixed(1), "Score"]}
              />
              <Line
                type="monotone"
                dataKey="score"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={{ r: 2.5, fill: "hsl(var(--primary))" }}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
